import React, { ReactNode, useCallback, useContext, useState } from 'react';
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { Button, Collapse, Nav, Navbar, NavbarBrand, NavbarText, NavbarToggler, NavItem, NavLink } from 'reactstrap';
import { AuthenticationContext, AuthenticationStatus } from '../component/AuthenticationContext';
import { IconSolidSignOutAlt } from '../component/Icons';
import { NotificationList } from '../component/notification';
import Secured, { ROLE_ADMIN } from '../component/Secured';
import { User } from '../type/Types';

interface SecuredMenuLinkProps {
  url: string;
  authority?: string;
  onClick?: () => void;
  children: ReactNode;
}

function SecuredMenuLink({ url, authority, onClick, children }: SecuredMenuLinkProps) {
  if (authority) {
    return (
      <Secured authority={authority}>
        <MenuLink url={url} onClick={onClick} children={children} />
      </Secured>
    );
  }

  return <MenuLink url={url} onClick={onClick} children={children} />;
}

interface MenuLinkProps {
  url: string;
  children: ReactNode;
  onClick?: () => void;
}

function MenuLink({ url, onClick, children }: MenuLinkProps) {
  let onClickStable = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  return (
    <NavItem>
      <NavLink tag={RouterNavLink} activeClassName="active" to={url} onClick={onClickStable}>
        {children}
      </NavLink>
    </NavItem>
  );
}

function getEnv() {
  if (window.location.host === 'bo.sokol-podoli.info' || window.location.host === 'muj.sokol-podoli.info') {
    return 'prod';
  }

  if (window.location.host === 'bo.sokol.svetrikovi.cz' || window.location.host === 'muj.sokol.svetrikovi.cz') {
    return 'test';
  }

  return 'local';
}

function getEnvColor(user: User | undefined) {
  const defaultColor = { color: 'dark', dark: true };

  if (!user) {
    return defaultColor;
  }

  if (!user.authorities.includes(ROLE_ADMIN)) {
    return defaultColor;
  }

  const env = getEnv();

  switch (env) {
    case 'prod':
      return { color: 'danger', dark: true };
    case 'test':
      return { color: 'warning', light: true };
    case 'local':
      return defaultColor;
  }
}

interface MenuItem {
  url: string;
  label: string;
  authority?: string;
}

interface CommonHeaderProps {
  menuItems: MenuItem[];
  expand: string;
  brandClassName?: string;
  lastNameClassName?: string;
}

export default function CommonHeader({ menuItems, expand, brandClassName, lastNameClassName }: CommonHeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { status, user } = useContext(AuthenticationContext);
  const authenticated = status === AuthenticationStatus.AUTHENTICATED;

  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  const collapse = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const envColor = getEnvColor(user);

  return (
    <>
      <Navbar {...envColor} expand={expand} sticky="top" className="mb-3">
        {/*hide brand on small screens*/}
        <NavbarBrand className={authenticated ? brandClassName : ''} href="/">
          Sokol databáze
        </NavbarBrand>
        {authenticated && (
          <>
            <NavbarToggler onClick={() => setIsOpen(prevIsOpen => !prevIsOpen)} />

            <Collapse isOpen={isOpen} navbar>
              <Nav navbar>
                {menuItems.map(({ url, label, authority }) => (
                  <SecuredMenuLink key={url} authority={authority} url={url} onClick={collapse}>
                    {label}
                  </SecuredMenuLink>
                ))}
              </Nav>
            </Collapse>
          </>
        )}

        <Nav className="ml-auto" navbar>
          {authenticated && user ? (
            <NavbarText>
              <span className={lastNameClassName}>{user.lastName}</span> {user.firstName}{' '}
              <NavLink
                tag={RouterNavLink}
                activeClassName="active"
                // do not go to new line on collapsed navbar
                className="d-inline"
                to="/logout"
                title="Odhlásit"
              >
                <IconSolidSignOutAlt />
              </NavLink>
            </NavbarText>
          ) : (
            // do not show login button on login page itself
            !isLoginPage && (
              <Button color="primary" tag={RouterNavLink} to="/login">
                Přihlásit
              </Button>
            )
          )}
        </Nav>
      </Navbar>
      <NotificationList />
    </>
  );
}
